import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, styled, type Theme } from "@mui/material/styles";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import { useState } from "react";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { DateCell } from "_shared/_views/tables/date-cells";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";
import { StatusChip } from "_shared/_views/tables/status-chip";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { formatDate } from "_shared/formatting/dates";
import { invitationStatusTone } from "legal-entities/invitations/statuses/invitation-status-tone";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
  searchTextFieldProps,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import {
  EntityOfficerInvitationOrderField,
  type OpenOfficerInvitationStatus,
  useCancelEntityOfficerInvitationMutation,
  useGetOpenOfficerInvitationsQuery,
} from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation, {
  EntityOfficerInvitationNotOpenError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";

const InvitationsTable = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  padding: theme.spacing(1),
  position: "relative",
  width: "100%",
}));

const InvitationsTableTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

const NewChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  verticalAlign: "middle",
}));

/** What the table lists, in the words its search box and its empty states are built from. */
export const INVITATIONS_SUBJECT = "officer invitations";

/** The Name column's id: the invitee, with their organization under the name (#6722). */
export const NAME_COLUMN = "name";
/** The Email column's id. It folds under the name when the pane is short of room (#6722). */
export const EMAIL_COLUMN = "email";
/** The Message column's id. */
export const MESSAGE_COLUMN = "message";
/** The Status column's id, and the column whose filter the server applies. */
export const STATUS_COLUMN = "status";
/** The Sent column's id, and the one column the server sorts by. */
export const SENT_COLUMN = "sent";

/** The statuses an open invitation can have, and so the only ones worth offering as a filter. */
const OPEN_STATUSES: OpenOfficerInvitationStatus[] = [InvitationStatus.Open, InvitationStatus.Expired];

/** Newest first, the server's own default. Never empty: the server orders every answer. */
const DEFAULT_SORTING: SortingState = [{ id: SENT_COLUMN, desc: true }];

export const DEFAULT_PAGE_SIZE = LIST_PAGE_SIZE;

/** The server's order for the table's sort state. Date sent is the only column it orders by. */
export function officerInvitationOrderFrom(sorting: SortingState): {
  orderBy: EntityOfficerInvitationOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy: EntityOfficerInvitationOrderField.DateSent,
    orderDir: first && !first.desc ? OrderDirection.Ascending : OrderDirection.Descending,
  };
}

/** The status picked in the Status column's filter, if it is one the server filters on. */
export function officerInvitationStatusFrom(columnFilters: ColumnFiltersState): OpenOfficerInvitationStatus | undefined {
  const value = columnFilters.find((filter) => filter.id === STATUS_COLUMN)?.value;
  return OPEN_STATUSES.find((status) => status === value);
}

/** The invitee's name, first then last, or nothing where the invitation names nobody. */
function inviteeName(invitation: EntityOfficerInvitation): string {
  return [invitation.registrationInfo?.name?.firstName, invitation.registrationInfo?.name?.lastName]
    .filter(Boolean)
    .join(" ");
}

/** The invitee's email beside their name, or nothing where the email already stands in for the name. */
function emailBesideName(invitation: EntityOfficerInvitation): string | undefined {
  return inviteeName(invitation) ? invitation.registrationInfo?.email?.value : undefined;
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

/** The tint on a row sent this visit. The "New" chip carries the same meaning for anyone who cannot see it. */
function sentThisVisitStyle(theme: Theme) {
  const accentColor = alpha(theme.palette.info.main, 0.95);
  return {
    "& > td": {
      backgroundColor: alpha(theme.palette.info.main, 0.07),
      borderBottomColor: accentColor,
      boxShadow: `inset 0 -2px 0 ${accentColor}`,
    },
  };
}

const columnHelper = createMRTColumnHelper<EntityOfficerInvitation>();

type EntityOfficerInvitationsTableProps = {
  onInvitationRemoved?: (invitation: EntityOfficerInvitation) => void;
  /** Ids of invitations sent from this page since it opened. Those rows are highlighted. */
  sentThisVisitIds?: ReadonlySet<string>;
};

function ignoreRemovedInvitation() {}

/**
 * Every open officer invitation, for the super user's organisations page.
 *
 * The search, the status, the order and the page all go to `legal-entities/officer-invitations`
 * (#5448). Before that the endpoint answered with every open invitation and the table drew them all.
 */
export default function EntityOfficerInvitationsTable(props: Readonly<EntityOfficerInvitationsTableProps>) {
  // Cancelling invalidates the open-invitation list, so the table needs no notification. The
  // callback stays for a caller that tracks removals itself.
  const onInvitationRemoved = props.onInvitationRemoved ?? ignoreRemovedInvitation;
  const sentThisVisitIds = props.sentThisVisitIds;

  const confirm = useConfirmDialog();
  const [cancelEntityOfficerInvitation] = useCancelEntityOfficerInvitationMutation();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });

  const order = officerInvitationOrderFrom(sorting);
  const {
    currentData: page,
    data: lastAnswer,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetOpenOfficerInvitationsQuery({
    search: globalFilter,
    status: officerInvitationStatusFrom(columnFilters),
    orderBy: order.orderBy,
    orderDir: order.orderDir,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  // Cancelling the only invitation on the last page leaves that page empty. Step back to the new
  // last page rather than show an empty table with invitations behind it. Adjusted while rendering
  // rather than in an effect, so the empty page is never drawn.
  const totalPages = page?.headers.totalPages ?? 0;
  if (totalPages > 0 && pagination.pageIndex >= totalPages) {
    setPagination((current) => ({ ...current, pageIndex: totalPages - 1 }));
  }

  async function handleCancelInvite(invitation: EntityOfficerInvitation) {
    const response = await confirm({
      title: "Cancel Invitation?",
      message: "Are you sure you want to cancel this entity officer invitation?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await cancelEntityOfficerInvitation({ invitation }).unwrap();
      onInvitationRemoved(invitation);
      enqueueSnackbar("Canceled entity officer invitation", { variant: "success" });
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        ex instanceof EntityOfficerInvitationNotOpenError
          ? ex.message
          : "Failed to cancel entity officer invitation",
        { variant: "error" },
      );
    }
  }

  const isSentThisVisit = (invitation: EntityOfficerInvitation) => {
    const invitationId = invitation.id?.value;
    return invitationId ? (sentThisVisitIds?.has(invitationId) ?? false) : false;
  };

  // A new search, filter or order is a new list, and a new list starts on its first page.
  const showFirstPage = () => setPagination((current) => ({ ...current, pageIndex: 0 }));

  const options: MRT_TableOptions<EntityOfficerInvitation> = {
    columns: [
      // The invitee, with their organization under the name rather than in a column of its own (#6722).
      // The search matches the invitee's email address, name and organization, so each marks it (#6590).
      columnHelper.accessor(inviteeName, {
        header: "Name",
        id: NAME_COLUMN,
        enableColumnFilter: false,
        enableSorting: false,
        meta: { kind: "name" },
        Cell: ({ row, table }) => {
          const invitation = row.original;
          const name = inviteeName(invitation);
          return (
            <CondensedCell
              title={
                <>
                  {/* An invitation that names nobody is read by its email address. */}
                  <SearchMatch table={table} text={name || invitation.registrationInfo?.email?.value} />
                  {isSentThisVisit(invitation) && <NewChip color="info" label="New" size="small" variant="outlined" />}
                </>
              }
              secondary={<SearchMatch table={table} text={invitation.entityName?.value} />}
            />
          );
        },
      }),
      // Its own column where there is room, and under the name where there is not (#6722). An invitation
      // that names nobody already reads by its email in Name, so it is not drawn a second time.
      columnHelper.accessor((invitation) => invitation.registrationInfo?.email?.value ?? "", {
        header: "Email",
        id: EMAIL_COLUMN,
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          kind: "text",
          fold: { into: NAME_COLUMN, priority: 1, render: emailBesideName },
        },
        Cell: ({ row, table }) => <SearchMatch table={table} text={emailBesideName(row.original)} />,
      }),
      columnHelper.accessor((invitation) => invitation.status, {
        header: "Status",
        id: STATUS_COLUMN,
        // Not searched, so never marked as matching; nor are Sent and Message.
        enableGlobalFilter: false,
        enableSorting: false,
        filterVariant: "select",
        filterSelectOptions: OPEN_STATUSES,
        meta: { kind: "status" },
        Cell: ({ row }) => (
          <StatusChip label={row.original.status} tone={invitationStatusTone(row.original.status)} />
        ),
      }),
      columnHelper.accessor((invitation) => formatDate(invitation.sentDate) ?? "", {
        header: "Sent",
        id: SENT_COLUMN,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.sentDate} />,
      }),
      columnHelper.accessor((invitation) => invitation.message?.value ?? "", {
        header: "Message",
        id: MESSAGE_COLUMN,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        meta: { kind: "text" },
        // One line, ending in an ellipsis, with the whole message on hover or keyboard focus (#6722): the
        // row opens nothing else that shows it.
        Cell: ({ row }) =>
          row.original.message?.value ? (
            <TruncatedCell focusable>{row.original.message.value}</TruncatedCell>
          ) : (
            <NoValueDash />
          ),
      }),
    ],
    data: page?.data ?? [],
    enableColumnFilters: true,
    enableColumnPinning: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableStickyHeader: true,
    // The server orders by one column, so a second sort indicator would promise an order it never sends.
    enableMultiSort: false,
    // The server sorts every answer, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    getRowId: (invitation, index) => invitation.id?.value ?? `skeleton-${index}`,
    initialState: { columnPinning: { right: ["mrt-row-actions"] }, showGlobalFilter: true },
    positionActionsColumn: "last",
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => searchTextFieldProps({ searches: INVITATIONS_SUBJECT, table }),
    muiTableBodyRowProps: ({ row }) => {
      const sentThisVisit = isSentThisVisit(row.original);
      return {
        className: sentThisVisit ? "active-officer-invitation just-sent-officer-invitation" : "active-officer-invitation",
        "data-invitation-id": row.original.id?.value,
        sx: sentThisVisit ? sentThisVisitStyle : undefined,
      };
    },
    // MRT lays its top toolbar out as a grid whose inner Box keeps `min-width: auto`; a zero floor is
    // what lets the one row wrap. See docs/software-design/table-toolbars.md.
    muiTopToolbarProps: { sx: { "& > .MuiBox-root": { minWidth: 0 } } },
    renderEmptyRowsFallback: emptyRowsFallback<EntityOfficerInvitation>({
      subject: INVITATIONS_SUBJECT,
      // `isFetching`, not `isLoading`: every page, sort and keystroke empties the table while it is
      // in flight, and a message drawn then says "nothing matched" about rows on their way.
      isLoading: isFetching,
      isError,
      onRetry: () => void refetch(),
    }),
    renderRowActions: ({ row }) => {
      const invitation = row.original;
      const email = invitation.registrationInfo?.email?.value ?? "";
      return (
        <RowActions
          ariaLabel={`Actions for the invitation to ${email}`}
          actions={[
            {
              key: "cancel",
              label: "Cancel invitation",
              ariaLabel: `Cancel the invitation to ${email}`,
              icon: <CancelOutlinedIcon />,
              destructive: true,
              onClick: () => void handleCancelInvite(invitation),
              // The server cancels only an open invitation, so an expired one would be refused.
              disabled: invitation.status !== InvitationStatus.Open,
              disabledReason: "Only an open invitation can be canceled",
            },
          ]}
        />
      );
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    // An empty fragment rather than `null`: MRT reads this with `??`, so a nullish answer restores
    // its own default button set and the toolbar ends up with two of everything.
    renderToolbarInternalActions: () => <></>,
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      showFirstPage();
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      showFirstPage();
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      showFirstPage();
    },
    // Every invitation that matches, from X-Pagination. While a page the cache has never held loads,
    // currentData is undefined, so the last answer's total holds the pager in place until it lands.
    rowCount: (page ?? lastAnswer)?.headers.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showProgressBars: isFetching,
      sorting,
    },
  };

  return (
    <InvitationsTable>
      <InvitationsTableTitle variant="h6">Entity Officer Invitations</InvitationsTableTitle>
      <MRTNoMemo<EntityOfficerInvitation> options={options} cards={{ defaultSorting: DEFAULT_SORTING }} />
    </InvitationsTable>
  );
}
